import { theme, FONT_RUNE } from "./theme";
import { useGame } from "./store";
import type { Capture } from "../engine";

export function CaptureChoice() {
  const pending = useGame((s) => s.pending);
  const declare = useGame((s) => s.declareCapture);
  const skip = useGame((s) => s.skipCaptures);

  if (!pending || pending.declarable.length === 0) return null;

  return (
    <div
      style={{
        // Small floating popup on the left side of the play area instead of a wide bottom sheet.
        position: "absolute",
        left: 8,
        top: "50%",
        transform: "translateY(-50%)",
        width: 280,
        maxWidth: "calc(100% - 16px)",
        maxHeight: "calc(100% - 16px)",
        background: theme.bgElev,
        border: `1px solid ${theme.accent}`,
        borderRadius: 8,
        padding: 12,
        boxShadow: "0 6px 20px rgba(0,0,0,0.6)",
        overflowY: "auto",
        zIndex: 10,
        // Box-sizing so the 1px border doesn't push children over the 280px boundary.
        boxSizing: "border-box",
      }}
    >
      <div
        className="rune"
        style={{ color: theme.accentSoft, fontSize: 13, marginBottom: 8, letterSpacing: 1 }}
      >
        Capture available — declare or skip
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {pending.declarable.map((c, ix) => (
          <CaptureRow key={ix} capture={c} onDeclare={(adv) => declare(c, adv)} />
        ))}
      </div>
      <button onClick={() => skip()} style={skipButton}>
        Skip · End turn
      </button>
    </div>
  );
}

function CaptureRow({
  capture,
  onDeclare,
}: {
  capture: Capture;
  onDeclare: (advance: boolean) => void;
}) {
  const single = capture.attackers.length === 1;
  const advanceable = capture.method === "encounter" || capture.method === "deceit";

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        padding: "8px 10px",
        background: theme.bg,
        border: `1px solid ${theme.inkSoft}`,
        borderRadius: 6,
      }}
    >
      {/* Method + arithmetic */}
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <span
          className="rune"
          style={{ fontSize: 13, color: theme.parchment, letterSpacing: 1 }}
        >
          {capture.method.toUpperCase()} · {capture.formula}
        </span>
        <span style={{ fontSize: 12, color: theme.boardLabel }}>
          target {squareLabel(capture.target)} · attackers{" "}
          {capture.attackers.map(squareLabel).join(", ")}
        </span>
      </div>

      {/* Buttons, stacked under the row instead of beside it — fits comfortably in the 280px
          popup, and gives space for descriptive labels (the old single-word "Hold" / "Advance"
          buttons were too cryptic about what each option actually does). */}
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {advanceable && single ? (
          <>
            <button
              onClick={() => onDeclare(false)}
              style={primaryActionButton}
              title="Take the enemy piece. Your attacker stays where it is."
            >
              Take piece — attacker holds
            </button>
            <button
              onClick={() => onDeclare(true)}
              style={secondaryActionButton}
              title="Take the enemy piece AND move your attacker into the captured square."
            >
              Take piece & advance into square
            </button>
          </>
        ) : (
          <button
            onClick={() => onDeclare(false)}
            style={primaryActionButton}
            title="Take the enemy piece."
          >
            Take piece
          </button>
        )}
      </div>
    </div>
  );
}

function squareLabel(sq: { file: number; rank: number }) {
  return `${"ABCDEFGH"[sq.file]}${sq.rank + 1}`;
}

const actionButtonBase: React.CSSProperties = {
  fontFamily: FONT_RUNE,
  fontSize: 12,
  letterSpacing: 1,
  padding: "8px 10px",
  borderRadius: 4,
  width: "100%",
  cursor: "pointer",
  boxSizing: "border-box",
};

const primaryActionButton: React.CSSProperties = {
  ...actionButtonBase,
  background: theme.accent,
  color: theme.bg,
  border: `1px solid ${theme.accent}`,
};

const secondaryActionButton: React.CSSProperties = {
  ...actionButtonBase,
  background: theme.boardEdge,
  color: theme.parchment,
  border: `1px solid ${theme.inkSoft}`,
};

const skipButton: React.CSSProperties = {
  marginTop: 10,
  width: "100%",
  padding: "8px 12px",
  background: theme.boardEdge,
  color: theme.parchment,
  border: `1px solid ${theme.inkSoft}`,
  borderRadius: 6,
  fontFamily: FONT_RUNE,
  fontSize: 13,
  cursor: "pointer",
  boxSizing: "border-box",
};
